import React, { useState, useEffect } from 'react';

// Dynamic asset allocation from your local assets workspace
import agribusinessImg from '../assets/banner6.jpg';
import waterImg from '../assets/banner3.jpg';
import investmentsImg from '../assets/banner4.jpg';

export default function Who() {
  const serviceRecap = [
    {
      title: "Agribusiness Value Addition",
      segment: "Fruit & Vegetable Dehydration",
      img: agribusinessImg,
      color: "#ED8936" // Orange Accent
    },
    {
      title: "Water Infrastructure",
      segment: "Professional Well Drilling",
      img: waterImg,
      color: "#38A169" // Green Accent
    },
    {
      title: "Strategic Investments",
      segment: "Venture Expansion Portfolios",
      img: investmentsImg,
      color: "#2B6CB0" // System Blue Accent
    }
  ];

  const [activeSlide, setActiveSlide] = useState(0);

  // Micro-slider state engine loop running every 3.5 seconds
  useEffect(() => {
    const recapTimer = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % serviceRecap.length);
    }, 3500);
    return () => clearInterval(recapTimer);
  }, [serviceRecap.length]);

  return (
    <section style={styles.whoWeAreSection} id="identity" className="py-5">
      <div className="container my-lg-5">
        <div className="row g-5 align-items-center">
          
          {/* LEFT COLUMN: Clean, Minimalist Text Overview Block */}
          <div className="col-12 col-lg-6 text-start">
            <div style={styles.contentWrap}>
              <div style={styles.badgeLabel} className="text-uppercase mb-2">Our Profile</div>
              <h2 style={styles.sectionTitle} className="fw-medium text-white mb-4">Who We Are</h2>
              
              <div style={styles.textStack} className="d-flex flex-column gap-3 mb-4">
                <p style={styles.leadParagraph} className="m-0">
                  Tanzavibe Holdings Company Limited is a proud, Tanzanian-owned enterprise 
                  established to generate long-term, sustainable economic value across East Africa.
                </p>
                <p style={styles.bodyText} className="m-0">
                  Headquartered in Arusha, Tanzania, our operations are purposefully directed toward 
                  mitigating environmental challenges, reinforcing food security via modern agribusiness 
                  value addition, and expanding vital community access to clean water systems. 
                </p>
                <p style={styles.bodyText} className="m-0">
                  We match state-of-the-art engineering parameters with strong local agricultural 
                  partnerships to transform rich natural resource opportunities into shared, resilient 
                  prosperity for our nation's communities and commercial clients alike.
                </p>
              </div>

              {/* Minimalist Sub-Tagline */}
              <div style={styles.pillarTagline} className="d-inline-flex align-items-center gap-2 pt-3">
                <span style={styles.taglineLine}></span>
                <span className="text-uppercase fw-semibold tracking-wider">Driven By Operational Integrity</span>
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN: Modern Asymmetric Visual Layer with Slide Recap */}
          <div className="col-12 col-lg-6">
            <div style={styles.sliderContainer} className="position-relative overflow-hidden w-100">
              
              {/* Asset Slides Layer Mapping */}
              {serviceRecap.map((slide, index) => (
                <div
                  key={index}
                  style={{
                    ...styles.slideView,
                    backgroundImage: `url(${slide.img})`,
                    opacity: index === activeSlide ? 1 : 0,
                    transform: index === activeSlide ? 'scale(1)' : 'scale(1.04)',
                  }}
                  className="position-absolute w-100 h-100"
                />
              ))}

              {/* Protective Dark Color Vignette */}
              <div style={styles.sliderMask}></div>

              {/* Floating Ultra-Minimal Glass Card Overlay */}
              <div style={styles.glassRecapCard} className="position-absolute bottom-0 start-0 m-4 p-4 text-start">
                <h3 style={styles.recapTitle} className="text-white my-1 fw-medium">{serviceRecap[activeSlide].title}</h3>
                <div className="d-flex align-items-center gap-2 mt-2">
                  <span style={{ ...styles.colorDot, backgroundColor: serviceRecap[activeSlide].color }}></span>
                  <span style={styles.recapSegment}>{serviceRecap[activeSlide].segment}</span>
                </div>
              </div>

              {/* Discrete Slider Dash Navigation dots */}
              <div style={styles.dashCluster} className="position-absolute top-0 end-0 m-4 d-flex flex-column gap-2">
                {serviceRecap.map((_, idx) => (
                  <span
                    key={idx}
                    style={{
                      ...styles.dashIndicator,
                      backgroundColor: idx === activeSlide ? '#FFF' : 'rgba(255, 255, 255, 0.2)',
                      height: idx === activeSlide ? '20px' : '6px',
                    }}
                  />
                ))}
              </div>

            </div>
          </div>

        </div>
      </div>
    </section>
  );
}

const styles = {
  whoWeAreSection: {
    backgroundColor: '#0A0F18', // Deep Midnight Blue
    fontFamily: "'Libre Franklin', sans-serif",
  },
  contentWrap: {
    width: '100%',
  },
  badgeLabel: {
    fontSize: '11px',
    color: '#38A169', // Emerald Green Pillar Accent
    letterSpacing: '0.15em',
    fontWeight: '600',
  },
  sectionTitle: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: 'calc(2.2rem + 1vw)',
  },
  textStack: {
    color: '#CBD5E0',
  },
  leadParagraph: {
    fontSize: '17px',
    lineHeight: '1.7',
    color: '#E2E8F0',
    fontWeight: '400',
  },
  bodyText: {
    fontSize: '14.5px',
    lineHeight: '1.75',
    color: '#A0AEC0',
    fontWeight: '300',
  },
  pillarTagline: {
    fontSize: '11px',
    color: '#ED8936', // Muted Orange Highlight
    letterSpacing: '0.08em',
    cursor: 'default',
  },
  taglineLine: {
    width: '20px',
    height: '1px',
    backgroundColor: '#ED8936',
  },
  sliderContainer: {
    height: '420px',
    borderRadius: '4px',
    backgroundColor: '#111A2E',
    border: '1px solid rgba(30, 45, 74, 0.6)',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.4)',
  },
  slideView: {
    top: 0,
    left: 0,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    transition: 'opacity 1s cubic-bezier(0.16, 1, 0.3, 1), transform 1.2s cubic-bezier(0.16, 1, 0.3, 1)',
  },
  sliderMask: {
    position: 'absolute',
    inset: 0,
    background: 'linear-gradient(180deg, rgba(10, 15, 24, 0.1) 0%, rgba(10, 15, 24, 0.85) 100%)',
    zIndex: 1,
  },
  glassRecapCard: {
    zIndex: 2,
    backgroundColor: 'rgba(11, 26, 46, 0.75)',
    backdropFilter: 'blur(16px)',
    WebkitBackdropFilter: 'blur(16px)',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    borderRadius: '4px',
    maxWidth: 'calc(100% - 48px)',
  },
  recapIndicator: {
    fontSize: '9px',
    color: '#718096',
    letterSpacing: '0.12em',
  },
  recapTitle: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: '24px',
    letterSpacing: '0.01em',
  },
  colorDot: {
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    display: 'inline-block',
  },
  recapSegment: {
    fontSize: '12.5px',
    color: '#CBD5E0',
    fontWeight: '300',
  },
  dashCluster: {
    zIndex: 2,
  },
  dashIndicator: {
    width: '4px',
    borderRadius: '2px',
    display: 'block',
    transition: 'all 0.6s cubic-bezier(0.16, 1, 0.3, 1)',
  },
};